import { ecId, record, upper, type InventoryChange, type Row } from '../ability/context';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { readItemField } from '../compile/item-field-reader';
import { posCoord } from '../../shared/array-types';
import { normalizePhpListish } from '../../shared/php-listish';
import { findInventoryRow } from '../inventory/flag-adjust';
import { savedFloorInventory } from '../inventory/floor-save-plan';
import type { AppliedFrame, AppliedFrameResult, AppliedNewInventory, AppliedTouchedRow } from './applied-frame';
import type { ShapeshiftInventoryWrite } from '../statuses/shapeshift-inventory';
import type { WeaponInventoryChange } from '../actions/weapon-attack-inventory';
import { withInventoryEconomy } from '../inventory/applied-inventory-economy';
import { applyAppliedFrame } from './apply-applied-frame';
import { compileCreatureEquippedCounts } from '../compile/possessor-compile';

/** Install the form planner's exact saved columns before encoding the held rows. */
export function applyFormInventoryWrites(writes: readonly ShapeshiftInventoryWrite[], hosts: Iterable<Row>): AppliedTouchedRow[] {
    if (!writes.length) return [];
    const roots = [...hosts], rows: AppliedTouchedRow[] = [];
    for (const write of writes) {
        let item: Row | null = null;
        for (const host of roots) {
            item = findInventoryRow(host, write.inventory_id);
            if (item) break;
        }
        const saved = record(item?.inventory);
        if (!item || !saved) throw new Error('lost_shapeshift:inventory-not-held');
        const changes = write.kind === 'unequip_acquired' ? { equipped: '' } : { inventory_private: write.kind === 'absorb' ? 2 : 0 };
        Object.assign(saved, changes);
        rows.push({ table: 'inventory', row_id: String(readInventoryField(item, 'inventory_id')), row: saved });
    }
    return rows;
}

export function abilityInventoryDepartures(changes: readonly InventoryChange[] = []): string[] {
    return [...new Set(changes.flatMap(change => change.destruction?.frame.creaturesRemoved ?? []).map(upper))];
}

/** Encode already-decided damage and projectiles for ability, weapon and GM senders. Raw
 * catalogs receive SELECT definitions; only display births carry compiled ones. */
export function abilityInventoryFrame(frame: AppliedFrameResult, changes: readonly InventoryChange[], hosts: readonly Row[],
    definition: (id: string) => Row | null, heldInputs?: (id: string) => Row | undefined,
    projectiles: readonly WeaponInventoryChange[] = []): AppliedFrameResult {
    if ('cache_ref' in frame || !changes.length && !projectiles.length) return frame;
    const rows = new Map(frame.rows.map(row => [row.table + ':' + upper(row.row_id), row]));
    const deletes = new Map(frame.deletes.map(row => [row.table + ':' + upper(row.row_id), row]));
    const births = new Map((frame.new_inventory ?? []).map(birth => [upper(birth.entity_id), birth]));
    const catalog: Row = {};
    const find = (id: string): Row | null => hosts.map(host => findInventoryRow(host, id)).find(Boolean) ?? null;
    const savedNode = (node: Row, id: string): Row | null => {
        for (const host of hosts) {
            const raw = savedFloorInventory(host, node, id);
            if (raw) return raw;
            // A retired corpse may already be absent from the display roster;
            // its held SELECT memo still owns the spilled rows and their facets.
            for (const key of ['_entity_base', '_inventory_base']) {
                const base = record(record(host[key])?.base);
                for (const member of normalizePhpListish(base?.encounter_creatures_arr)) {
                    const held = findInventoryRow(member, id);
                    if (held) return held;
                }
            }
        }
        return null;
    };
    const saved = (node: Row, id: string): Row => record(savedNode(node, id)?.inventory) ?? record(node.inventory) ?? {};
    const rawDefinition = (id: string, template: Row | null): Row | null => {
        for (const host of [template, ...hosts]) for (const key of ['_entity_base', '_inventory_base']) {
            const base = record(record(host?.[key])?.base), raw = record(record(base?.catalog)?.[id]);
            if (raw && upper(readItemField(raw, 'item_id')) === id) return raw;
            if (base && upper(readItemField(base, 'item_id')) === id) return base;
        }
        const raw = heldInputs?.(id);
        return raw && upper(readItemField(raw, 'item_id')) === id ? raw : null;
    };
    const remove = (table: string, id: string): void => {
        id = upper(id); deletes.set(table + ':' + id, { table, row_id: id });
    };
    const put = (node: Row | null, id: string, changes: Row, position?: Row): void => {
        const prior = rows.get('inventory:' + upper(id));
        position ??= prior?.position ?? undefined;
        const row: AppliedTouchedRow = { table: 'inventory', row_id: upper(id), partial: true,
            row: { ...record(node?.inventory), ...prior?.row, ...changes },
            ...(node ? { base: { ...saved(node, id), ...prior?.base, ...changes } } : {}),
            ...(position ? { position } : {}) };
        rows.set('inventory:' + upper(id), row);
    };
    const birth = (source: Row, additions: Row = {}): void => {
        const base: Row[] = [], compiled: Row = {};
        const visit = (node: Row, root = false): Row | null => {
            const id = upper(readInventoryField(node, 'inventory_id'));
            if (!id || Number(readInventoryField(node, 'inventory_deleted') ?? 0) === 1 || deletes.has('inventory:' + id)) return null;
            const sourceBase = savedNode(node, id);
            const inventory = { ...record(sourceBase?.inventory), ...record(node.inventory), ...(root ? additions : {}) };
            const item = upper(readInventoryField({ inventory }, 'inventory_item_id')), template = definition(item);
            if (template) compiled[item] = template;
            const raw = rawDefinition(item, template);
            if (raw) catalog[item] = raw;
            const position = Object.fromEntries((['x_pos', 'y_pos', 'z_pos'] as const).map(key => [key,
                root && key in additions ? additions[key] : posCoord(node, key) ?? 0]));
            const rawRow: Row = { ...sourceBase, inventory: { ...inventory, ...position } };
            delete rawRow.inventory_in_container;
            base.push(rawRow);
            const children = readInventoryRows(node, 'inventory_in_container').flatMap(child => {
                const projected = visit(child); return projected ? [projected] : [];
            });
            const projected: Row = { ...node, inventory, position };
            delete projected.catalog;
            if ('inventory_in_container' in node) projected.inventory_in_container = children;
            return projected;
        };
        const node = visit(source, true);
        if (!node) return;
        node.catalog = compiled;
        const id = upper(readInventoryField(node, 'inventory_id'));
        births.set(id, { entity_id: id, possess_id: upper(readInventoryField(node, 'possess_id')), base, node } satisfies AppliedNewInventory);
    };
    for (const change of changes) {
        const node = find(change.inventory_id);
        put(node, change.inventory_id, change.changes, change.movement);
        const destruction = change.destruction;
        if (!destruction) continue;
        const placements = new Map<string, Row>();
        const placedRows = (command: typeof destruction): void => {
            for (const placed of [command.rubble, command.waypoint])
                if (placed?.saved_row) placements.set(upper(placed.inventory_id), placed.saved_row);
            for (const child of command.spill?.destroyed ?? []) placedRows(child);
        };
        placedRows(destruction);
        for (const id of destruction.frame.remove) remove('inventory', id);
        for (const id of destruction.frame.creaturesRemoved ?? []) remove('encounter_creatures', id);
        for (const changed of destruction.frame.vitality ?? []) put(find(changed.inventory_id), changed.inventory_id, { v_current: changed.v_current });
        for (const added of destruction.frame.add) {
            const id = upper(readInventoryField(added as unknown as Row, 'inventory_id'));
            birth(added as unknown as Row, placements.get(id));
        }
        for (const released of destruction.frame.released ?? []) {
            birth(released);
            // Retire its former placement as well as installing the floor birth.
            remove('inventory', upper(readInventoryField(released, 'inventory_id')));
        }
    }
    for (const change of projectiles) {
        const source = find(change.inventory_id), recovery = change.recovery;
        if (!recovery || upper(recovery.inventory_id) !== upper(change.inventory_id)) {
            put(source, change.inventory_id, change.after);
            if (change.after.inventory_deleted) remove('inventory', change.inventory_id);
        }
        if (!recovery) continue;
        const arrival = { possess_id: recovery.possess_id, item_qty: recovery.item_qty, inventory_deleted: 0, equipped: '',
            inventory_wares: 0, x_pos: recovery.x_pos, y_pos: recovery.y_pos, z_pos: recovery.z_pos };
        put(find(recovery.inventory_id) ?? recovery.row, recovery.inventory_id, arrival,
            { x_pos: recovery.x_pos, y_pos: recovery.y_pos, z_pos: recovery.z_pos });
        if (recovery.created || upper(recovery.inventory_id) === upper(change.inventory_id)
            && upper(change.before.possess_id) !== upper(recovery.possess_id)) {
            // Only the arriving item borrows its held display/economy; the recovery owns its new identity and placement.
            const node = source ? { ...source, ...recovery.row, // dnr-clone: allow size=one arriving inventory node; why=borrow the transferred item's display leaves and retain the recovery's exact identity
                computed: { ...record(source.computed), ...record(recovery.row.computed) } } : recovery.row; // dnr-clone: allow size=two inventory computed maps; why=the arriving item's shell borrows unchanged leaves without compiling its former holder
            birth(node, arrival);
        }
    }
    const result: AppliedFrame = { ...frame, rows: [...rows.values()].filter(row => !deletes.has(row.table + ':' + upper(row.row_id))),
        deletes: [...deletes.values()], new_inventory: [...births.values()],
        ...(Object.keys(catalog).length ? { inputs: { ...frame.inputs, catalog: { ...record(frame.inputs?.catalog), ...catalog } } } : {}) };
    if (!projectiles.length) return result;
    const quantities = new Map(projectiles.flatMap(change => [[upper(change.inventory_id), change.before.item_qty] as const,
        ...(change.recovery?.created ? [[upper(change.recovery.inventory_id), change.before.item_qty] as const] : [])]));
    const next = withInventoryEconomy(result, hosts, quantities);
    const holders = new Set(projectiles.flatMap(change => [upper(change.before.possess_id), upper(change.recovery?.possess_id)]));
    const members = new Map(hosts.flatMap(host => ecId(host) ? [[ecId(host), host] as const]
        : normalizePhpListish(host.encounter_creatures_arr).filter((row): row is Row => !!record(row)).map(row => [ecId(row), row] as const)));
    for (const id of holders) {
        const member = members.get(id);
        if (!member) continue;
        const projected = applyAppliedFrame(next, member, false, false).value as Row;
        const equipped = compileCreatureEquippedCounts(projected, { definition, catalog: record(member.catalog) ?? undefined }, { retainEmpty: true });
        if (!equipped) continue;
        const prior = next.computed_delta?.[id];
        next.computed_delta = { ...next.computed_delta, [id]: { ...prior, set: [...prior?.set ?? [], { path: ['creature', 'equipped'], value: equipped }], unset: prior?.unset ?? [] } };
    }
    return next;
}
